/**
 * `deriveOnvifAuthPromptKind` — pure helper that resolves the
 * OnvifAuthPrompt `kind` discriminator from the paused-bulk-add status
 * + the store's bulkDiscoveries mirror.
 *
 * Per PRD §2.8 — Prompt wording refinement: the `<OnvifAuthPrompt>` is
 * reused for ONVIF probes AND Iris-enabled device-link operations
 * (always-prompt path in wiring.ts). The legacy caveat copy mentions
 * "ONVIF camera" which is incorrect when the prompt is rendered for an
 * Iris-enabled link. The copy is split via a `kind` prop on the
 * component; this helper picks the right kind by name-matching the
 * paused discovery against the mirrored `bulkDiscoveries` slice and
 * reading the `irisEnabled` flag.
 *
 * Per `think-in-systems` (ai.knowledge/global): the kind resolution is
 * a pure function — kept here for unit-test isolation (the scaffold
 * test can mount with a fixture status + mirror without bringing the
 * full zustand store into scope).
 *
 * Per `verify-and-escalate` (ai.knowledge/global): defaults to
 * `'onvif'` when:
 *   - The status payload already carries an explicit `kind` (forward-
 *     compat: when wiring.ts gains a `kind:` setter on the
 *     `awaiting-auth` payload, the explicit value wins).
 *   - The paused name does not match any discovery (cold cache, race
 *     between status setter and selector mirror) — fall back to the
 *     backward-compatible ONVIF copy.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
import type { Discovery } from '#~/device/discovery/index.js';
import type { OnvifAuthPromptKind } from './onvif-auth-prompt.jsx';

/**
 * Narrowed shape of `BulkAddStatus.awaiting-auth` — the scaffold call
 * site only passes the name to the prompt; we accept the same shape
 * plus an optional forward-compat `kind`.
 */
export interface AwaitingAuthStatusLike {
  kind: 'awaiting-auth';
  name: string;
  // Optional forward-compat field; wiring.ts may populate this on the
  // status payload in a future wave so the kind is set at the producer
  // side rather than re-derived in scaffold.tsx. When present it wins.
  promptKind?: OnvifAuthPromptKind;
}

/**
 * Resolve the OnvifAuthPrompt `kind` from the status + discoveries
 * mirror.
 *
 * Lookup order:
 *   1. `status.promptKind` (forward-compat) — explicit wins.
 *   2. Match the status name to a discovery in `bulkDiscoveries`
 *      (case-sensitive — discovery names come from the wire and are
 *      stable strings). If the matched discovery has
 *      `irisEnabled === true`, return `'iris-enabled'`.
 *   3. Otherwise → `'onvif'`.
 *
 * Copy must NOT reference "ONVIF camera" for Iris-enabled discoveries.
 * This resolution is the boundary.
 */
export const deriveOnvifAuthPromptKind = (
  status: AwaitingAuthStatusLike,
  bulkDiscoveries: readonly Discovery[] | undefined
): OnvifAuthPromptKind => {
  // Forward-compat: an explicit kind on the status payload always wins.
  // Useful when wiring.ts later evolves to set the kind on the
  // Iris-enabled vs ONVIF branches directly.
  if (status.promptKind === 'iris-enabled' || status.promptKind === 'onvif') {
    return status.promptKind;
  }

  // Name-based lookup against the mirror selector.tsx populates. The
  // mirror covers both raw discoveries AND iris-service-derived rows
  // (selector.tsx ~line 463 — `[...discoveries, ...irisDiscoveries]`),
  // so the Iris-enabled branch resolves correctly even though Iris
  // services arrive on a separate stream from the ONVIF discoveries.
  const match = bulkDiscoveries?.find((d) => d.name === status.name);
  if (match?.irisEnabled === true) {
    return 'iris-enabled';
  }

  // Default: ONVIF — backward-compatible with the Multi-Add Phase 1
  // ONVIF retry-fail call site, which is the only producer of
  // `awaiting-auth` outside the Iris-enabled branch.
  return 'onvif';
};
